import { useState } from "react";
import { useAnmeldung } from "../zustand/Anmeldung";
import { pruefeCode } from "../dienst/server";
import { Haken, Warnung } from "../bausteine/Symbole";

/*
  Das Tor zur Anwendung.

  Drei Zustände in einem Rahmen:
    anmelden      E-Mail und Passwort
    registrieren  erst Code, dann die eigenen Angaben
    passwort      Rücksetz-Code und neues Passwort

  Links steht die Erklärung, rechts das Formular. Beim Registrieren sind das
  die drei Schritte, damit jemand, der zum ersten Mal davorsitzt, weiß, was
  gleich passiert und wann es vorbei ist.
*/

const MINDESTLAENGE_PASSWORT = 10;

export default function Anmelden() {
  const [zustand, setzeZustand] = useState("anmelden");
  /* Liegt hier oben, damit die Erklärung links mitwandert. */
  const [schritt, setzeSchritt] = useState(1);

  function wechsle(neu) {
    setzeSchritt(1);
    setzeZustand(neu);
  }

  return (
    <div className="tor">
      <div className="tor__karte">
        <Erklaerung zustand={zustand} schritt={schritt} />
        {zustand === "anmelden" && <AnmeldeFormular wechsle={wechsle} />}
        {zustand === "registrieren" && (
          <RegistrierFormular wechsle={wechsle} schritt={schritt} setzeSchritt={setzeSchritt} />
        )}
        {zustand === "passwort" && <PasswortFormular wechsle={wechsle} />}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------
   Links: Erklärung
   --------------------------------------------------------------- */

function Erklaerung({ zustand, schritt = 1 }) {
  return (
    <div className="tor__erklaerung">
      <p className="tor__marke">Fraktionsbüro</p>

      <Motiv />

      {zustand === "registrieren" ? (
        <ol className="tor__schritte">
          <Schritt nummer={1} aktuell={schritt} titel="Code eingeben">
            Den hast du von uns bekommen — auf einem Zettel oder per Nachricht.
            Er sagt uns, zu welcher Fraktion du gehörst.
          </Schritt>
          <Schritt nummer={2} aktuell={schritt} titel="E-Mail und Passwort festlegen">
            Dazu deinen Namen, damit die anderen sehen, wer schreibt. Ein Foto
            kannst du hinzufügen, musst du aber nicht.
          </Schritt>
          <Schritt nummer={3} aktuell={schritt} titel="Fertig">
            Ab jetzt reichen E-Mail und Passwort. Den Code brauchst du nie
            wieder.
          </Schritt>
        </ol>
      ) : (
        <p className="tor__schrittText" style={{ fontSize: "var(--schrift-normal)" }}>
          {zustand === "passwort"
            ? "Du hast von uns einen Rücksetz-Code bekommen. Damit vergibst du hier ein neues Passwort — danach ist der Code verbraucht."
            : "Alles an einem Ort: Sitzungsunterlagen lesen, Textstellen markieren, sich in der Fraktion abstimmen."}
        </p>
      )}
    </div>
  );
}

function Schritt({ nummer, aktuell, titel, children }) {
  const art =
    nummer < aktuell ? "tor__schritt--erledigt" : nummer > aktuell ? "tor__schritt--offen" : "";
  return (
    <li className={`tor__schritt ${art}`}>
      <span className="tor__nummer" aria-hidden="true">
        {nummer < aktuell ? <Haken groesse={14} /> : nummer}
      </span>
      <span>
        <span className="tor__schrittTitel">{titel}</span>
        <span className="tor__schrittText" style={{ display: "block" }}>
          {children}
        </span>
      </span>
    </li>
  );
}

/* Dasselbe Motiv wie das App-Symbol: eine Vorlage mit markierter Textstelle. */
function Motiv() {
  return (
    <svg className="tor__motiv" width="88" height="88" viewBox="0 0 88 88" aria-hidden="true">
      <rect x="18" y="10" width="52" height="68" rx="4" fill="#fff" stroke="currentColor" strokeWidth="2" />
      <g fill="currentColor" opacity="0.35">
        <rect x="27" y="22" width="30" height="4" rx="2" />
        <rect x="27" y="32" width="34" height="4" rx="2" />
        <rect x="27" y="52" width="32" height="4" rx="2" />
        <rect x="27" y="62" width="26" height="4" rx="2" />
      </g>
      <rect x="27" y="41" width="30" height="6" rx="3" fill="currentColor" />
    </svg>
  );
}

/* ---------------------------------------------------------------
   Wiederkehrende Teile
   --------------------------------------------------------------- */

function Feld({ name, hilfe, children }) {
  return (
    <label className="feld">
      <span className="feld__name">{name}</span>
      {children}
      {hilfe && <span className="feld__hilfe">{hilfe}</span>}
    </label>
  );
}

function PasswortFeld({ wert, aendere, name = "Passwort", hilfe }) {
  const [sichtbar, setzeSichtbar] = useState(false);
  return (
    <div className="feld">
      <span className="feld__name">{name}</span>
      <div className="feld__mitKnopf">
        <input
          className="feld__eingabe"
          type={sichtbar ? "text" : "password"}
          value={wert}
          onChange={(e) => aendere(e.target.value)}
          autoComplete="current-password"
        />
        <button
          type="button"
          className="feld__zeigen"
          onClick={() => setzeSichtbar((s) => !s)}
          aria-label={sichtbar ? "Passwort verbergen" : "Passwort anzeigen"}
        >
          {sichtbar ? "Verbergen" : "Zeigen"}
        </button>
      </div>
      {hilfe && <span className="feld__hilfe">{hilfe}</span>}
    </div>
  );
}

function Fehler({ text }) {
  if (!text) return null;
  return (
    <p className="tor__fehler" role="alert">
      <Warnung groesse={18} />
      <span>{text}</span>
    </p>
  );
}

/* ---------------------------------------------------------------
   Anmelden
   --------------------------------------------------------------- */

function AnmeldeFormular({ wechsle }) {
  const { aktionen } = useAnmeldung();
  const [email, setzeEmail] = useState("");
  const [passwort, setzePasswort] = useState("");
  const [fehler, setzeFehler] = useState("");
  const [laeuft, setzeLaeuft] = useState(false);

  async function absenden(e) {
    e.preventDefault();
    setzeFehler("");
    setzeLaeuft(true);
    try {
      await aktionen.anmelden(email.trim(), passwort);
    } catch (f) {
      setzeFehler(f.message);
      setzeLaeuft(false);
    }
  }

  return (
    <form className="tor__formular" onSubmit={absenden}>
      <div>
        <h1 className="tor__titel">Willkommen zurück</h1>
        <p className="tor__vorspann">Melde dich mit E-Mail und Passwort an.</p>
      </div>

      <Fehler text={fehler} />

      <Feld name="E-Mail-Adresse">
        <input
          className="feld__eingabe"
          type="email"
          value={email}
          onChange={(e) => setzeEmail(e.target.value)}
          autoComplete="username"
          inputMode="email"
        />
      </Feld>

      <PasswortFeld wert={passwort} aendere={setzePasswort} />

      <button className="knopf knopf--breit" type="submit" disabled={laeuft}>
        {laeuft ? "Einen Moment …" : "Anmelden"}
      </button>

      <div className="tor__wechsel">
        <span>Zum ersten Mal hier?</span>
        <button type="button" className="tor__verweis" onClick={() => wechsle("registrieren")}>
          Ich habe einen Einladungscode
        </button>
        <button type="button" className="tor__verweis" onClick={() => wechsle("passwort")}>
          Passwort vergessen
        </button>
      </div>
    </form>
  );
}

/* ---------------------------------------------------------------
   Registrieren
   --------------------------------------------------------------- */

function RegistrierFormular({ wechsle, schritt, setzeSchritt }) {
  const { aktionen } = useAnmeldung();

  const [code, setzeCode] = useState("");
  const [fraktion, setzeFraktion] = useState(null);

  const [name, setzeName] = useState("");
  const [email, setzeEmail] = useState("");
  const [passwort, setzePasswort] = useState("");
  const [bild, setzeBild] = useState(null);

  const [fehler, setzeFehler] = useState("");
  const [laeuft, setzeLaeuft] = useState(false);

  async function codePruefen(e) {
    e.preventDefault();
    setzeFehler("");
    setzeLaeuft(true);
    try {
      const antwort = await pruefeCode(code);
      setzeFraktion(antwort);
      setzeSchritt(2);
    } catch (f) {
      setzeFehler(f.message);
    } finally {
      setzeLaeuft(false);
    }
  }

  async function registrieren(e) {
    e.preventDefault();
    setzeFehler("");

    if (passwort.length < MINDESTLAENGE_PASSWORT) {
      setzeFehler(`Das Passwort braucht mindestens ${MINDESTLAENGE_PASSWORT} Zeichen.`);
      return;
    }

    setzeLaeuft(true);
    try {
      await aktionen.registrieren({ code, name: name.trim(), email: email.trim(), passwort });
      if (bild) {
        try {
          await aktionen.profilSpeichern({ bild });
        } catch {
          /* Das Foto ist freiwillig — daran soll die Anmeldung nicht scheitern. */
        }
      }
    } catch (f) {
      setzeFehler(f.message);
      setzeLaeuft(false);
    }
  }

  if (schritt === 1) {
    return (
      <form className="tor__formular" onSubmit={codePruefen}>
        <div>
          <h1 className="tor__titel">Einladungscode</h1>
          <p className="tor__vorspann">
            Acht Zeichen, die du von uns bekommen hast. Groß- und Kleinschreibung
            sind egal.
          </p>
        </div>

        <Fehler text={fehler} />

        <Feld name="Code" hilfe="Zum Beispiel FRKB-7QX4">
          <input
            className="feld__eingabe feld__eingabe--code"
            value={code}
            onChange={(e) => setzeCode(e.target.value)}
            placeholder="XXXX-XXXX"
            maxLength={12}
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck="false"
          />
        </Feld>

        <button className="knopf knopf--breit" type="submit" disabled={laeuft || !code.trim()}>
          {laeuft ? "Wird geprüft …" : "Weiter"}
        </button>

        <div className="tor__wechsel">
          <span>Schon ein Konto?</span>
          <button type="button" className="tor__verweis" onClick={() => wechsle("anmelden")}>
            Zur Anmeldung
          </button>
        </div>
      </form>
    );
  }

  return (
    <form className="tor__formular" onSubmit={registrieren}>
      <div>
        <h1 className="tor__titel">Deine Angaben</h1>
        <p className="tor__vorspann">
          Der Code gilt für <strong>{fraktion.fraktion}</strong>, {fraktion.kommune}.
        </p>
      </div>

      <Fehler text={fehler} />

      <Feld name="Vor- und Nachname" hilfe="So sehen dich die anderen in der Fraktion.">
        <input
          className="feld__eingabe"
          value={name}
          onChange={(e) => setzeName(e.target.value)}
          autoComplete="name"
        />
      </Feld>

      <Feld name="E-Mail-Adresse" hilfe="Damit meldest du dich künftig an.">
        <input
          className="feld__eingabe"
          type="email"
          value={email}
          onChange={(e) => setzeEmail(e.target.value)}
          autoComplete="email"
          inputMode="email"
        />
      </Feld>

      <PasswortFeld
        wert={passwort}
        aendere={setzePasswort}
        hilfe={`Mindestens ${MINDESTLAENGE_PASSWORT} Zeichen. Ein Satz, den du dir merken kannst, ist besser als ein kurzes Kauderwelsch.`}
      />

      <FotoFeld bild={bild} setzeBild={setzeBild} />

      <button
        className="knopf knopf--breit"
        type="submit"
        disabled={laeuft || !name.trim() || !email.trim() || !passwort}
      >
        {laeuft ? "Wird angelegt …" : "Konto anlegen und loslegen"}
      </button>

      <div className="tor__wechsel">
        <button
          type="button"
          className="tor__verweis"
          onClick={() => {
            setzeSchritt(1);
            setzeFehler("");
          }}
        >
          Zurück zum Code
        </button>
      </div>
    </form>
  );
}

function FotoFeld({ bild, setzeBild }) {
  return (
    <div className="feld">
      <span className="feld__name">Foto — freiwillig</span>
      <div className="foto">
        {bild ? (
          <img className="foto__bild" src={URL.createObjectURL(bild)} alt="" />
        ) : (
          <span className="foto__platzhalter" aria-hidden="true">
            ?
          </span>
        )}
        <div style={{ flex: 1 }}>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setzeBild(e.target.files[0] || null)}
            style={{ fontSize: "var(--schrift-klein)", maxWidth: "100%" }}
          />
          <p className="feld__hilfe" style={{ marginTop: "0.35rem" }}>
            Kannst du auch später im Profil hinzufügen. Überspringen ist völlig
            in Ordnung.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------
   Passwort zurücksetzen
   --------------------------------------------------------------- */

function PasswortFormular({ wechsle }) {
  const { aktionen } = useAnmeldung();
  const [code, setzeCode] = useState("");
  const [passwort, setzePasswort] = useState("");
  const [fehler, setzeFehler] = useState("");
  const [laeuft, setzeLaeuft] = useState(false);

  async function absenden(e) {
    e.preventDefault();
    setzeFehler("");
    if (passwort.length < MINDESTLAENGE_PASSWORT) {
      setzeFehler(`Das Passwort braucht mindestens ${MINDESTLAENGE_PASSWORT} Zeichen.`);
      return;
    }
    setzeLaeuft(true);
    try {
      await aktionen.passwortNeu(code, passwort);
    } catch (f) {
      setzeFehler(f.message);
      setzeLaeuft(false);
    }
  }

  return (
    <form className="tor__formular" onSubmit={absenden}>
      <div>
        <h1 className="tor__titel">Neues Passwort</h1>
        <p className="tor__vorspann">
          Mit dem Rücksetz-Code, den du von uns bekommen hast.
        </p>
      </div>

      <Fehler text={fehler} />

      <p className="tor__bestaetigung">
        <span>
          Du hast keinen Code? Melde dich bei uns — wir schicken dir einen. Einen
          Link per E-Mail gibt es noch nicht.
        </span>
      </p>

      <Feld name="Rücksetz-Code">
        <input
          className="feld__eingabe feld__eingabe--code"
          value={code}
          onChange={(e) => setzeCode(e.target.value)}
          placeholder="XXXX-XXXX"
          maxLength={12}
          autoCapitalize="characters"
          autoComplete="off"
          spellCheck="false"
        />
      </Feld>

      <PasswortFeld
        wert={passwort}
        aendere={setzePasswort}
        name="Neues Passwort"
        hilfe={`Mindestens ${MINDESTLAENGE_PASSWORT} Zeichen.`}
      />

      <button className="knopf knopf--breit" type="submit" disabled={laeuft || !code.trim()}>
        {laeuft ? "Wird gesetzt …" : "Passwort setzen und anmelden"}
      </button>

      <div className="tor__wechsel">
        <button type="button" className="tor__verweis" onClick={() => wechsle("anmelden")}>
          Zurück zur Anmeldung
        </button>
      </div>
    </form>
  );
}
